/**
 * THE FIELD THAT OBJECTED, BROUGHT TO THE READER — MR-12's other half.
 *
 * Both building forms already keep a list of invalid field names and already
 * render a banner above the card. What neither did was MOVE. The card is a
 * fixed-height scroller, so the field being complained about is routinely below
 * the fold: the banner said "One field needs attention", the reader saw no
 * field, and the form looked broken rather than picky. WP-32's banner fixed the
 * silence; it did not fix the distance.
 *
 * And a server-side rejection was worse than that. `properties.js` answers a bad
 * time zone with
 *
 *     { message: '"Amrica/New_York" is not a recognised time zone',
 *       field: 'timezone' }
 *
 * — it names the field — and the form threw the name away and raised a toast.
 * A toast is transient, sits in a corner, and is not attached to anything. So
 * the one piece of information that would have pointed at the typo was the one
 * piece being discarded. That is the MR-20 complaint in practice: the picker and
 * the validator both exist, and the answer still did not reach the field.
 *
 * `name` IS THE KEY. Both forms label their inputs with a `name` matching the
 * key the server and the local validator use (`property_name`, `timezone`,
 * `email`, `phone_number`), so one lookup serves both. `location` is composite
 * and carries no `name`, hence the fallbacks.
 *
 * Focus, not just scroll: `scrollIntoView` alone moves sighted readers and
 * nobody else. Focus moves the screen reader's cursor too and scrolls as a
 * side effect, so it is the operative call and the scroll is the fallback for
 * a container that focus does not reveal.
 */

/** What the server said, when it named a field. */
export type ServerFieldError = { field: string; message: string };

type MaybeAxios = {
  response?: { data?: { message?: unknown; field?: unknown } };
};

/**
 * Reads a field-scoped rejection out of an error. Returns null when the server
 * did not name a field, which is the signal to fall back to a toast — an error
 * with no field has nowhere to point.
 */
export const serverFieldError = (error: unknown): ServerFieldError | null => {
  const data = (error as MaybeAxios)?.response?.data;
  if (!data) return null;
  const field = typeof data.field === 'string' ? data.field : '';
  const message = typeof data.message === 'string' ? data.message : '';
  if (!field || !message) return null;

  return { field, message };
};

const candidates = (root: ParentNode, field: string): Element | null => {
  const escaped = field.replace(/"/g, '\\"');

  return (
    root.querySelector(`[name="${escaped}"]`) ||
    root.querySelector(`[data-field="${escaped}"]`) ||
    root.querySelector(`[id$="-${escaped}"]`) ||
    root.querySelector(`[id$="-${escaped.replace(/_/g, '-')}"]`)
  );
};

/**
 * Puts the reader on the field. Returns false when there is no such field on
 * screen, so the caller can still raise a toast rather than failing silently —
 * which is the failure mode this whole module exists to remove.
 */
export const focusField = (
  field: string,
  root: ParentNode = document
): boolean => {
  const el = candidates(root, field);
  if (!el) return false;

  /* Scroll first, then focus. `focus({ preventScroll: true })` inside a
     scrolling card can land the field flush against the top edge, under the
     sticky header; letting scrollIntoView place it with `block: 'center'` and
     then focusing keeps it where it can actually be read. */
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
  const focusable = el as HTMLElement;
  if (typeof focusable.focus === 'function') {
    focusable.focus({ preventScroll: true });
  }

  return true;
};

/**
 * The local-validation counterpart: go to the FIRST field that objected. First,
 * not the last and not an arbitrary one — a reader fixes a form top to bottom,
 * and being sent to the bottom-most complaint means scrolling back up past the
 * ones still outstanding.
 */
export const focusFirstInvalid = (
  fields: string[],
  root: ParentNode = document
): boolean => fields.some(field => focusField(field, root));
